"use client";

import { MapPin, PhoneCall } from "lucide-react";
import { Vehicle } from "../../../../types/Vehicle.types";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { useForm, SubmitHandler } from "react-hook-form";
import { ActionsTypeEnum, LeadFormDataType } from "./props";
import SendIcon from "../../../icons/send";
import { useToast } from "@ui/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { LeadFormSchema } from "./schema";
import { removeNonNumericAndSpaces } from "../../../../utils/common";
import { ContactDialog } from "./modal";
import { setCookie, getCookie } from "cookies-next";
import { useCreateLead } from "../../../../hooks";
import { LeadOrigin, LeadStatus } from "../../../../types/Leads.types";
import {
  PostHostEventsEnum,
  sendPostHogEvent,
} from "../../../../libs/post-hog/post-hog-send-event";
import { Team } from "database";

export const ContactActions = ({
  vehicle,
  vehicleTeam,
}: {
  vehicle: Vehicle;
  vehicleTeam: Team | null;
}) => {
  const teamSlug = vehicleTeam?.slug ?? null;

  const { seller } = vehicle;
  console.log(seller);
  const { location } = seller;
  const { toast } = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<ActionsTypeEnum | null>(
    null
  );

  const { createLead } = useCreateLead();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<LeadFormDataType>({
    criteriaMode: "firstError",
    reValidateMode: "onChange",
    resolver: zodResolver(LeadFormSchema),
    defaultValues: {
      hasTradeInVehicle: false,
    },
  });

  const address = [
    location.adressLine,
    location.number && `Nº ${location.number}`,
    location.district,
    location.city,
    location.state,
  ]
    .filter(Boolean)
    .join(", ");

  const googleMapsLink = `https://maps.google.com/maps?q=$${encodeURIComponent(
    `${location.adressLine}, ${location.number} - ${location.district}, ${location.city} - ${location.state}`
  )}`;

  const setLeadCookies = (data: LeadFormDataType) => {
    setCookie("leadName", data.name.toString(), {
      maxAge: 60 * 60 * 24 * 60,
    });

    setCookie("leadPhone", data.phone.toString(), {
      maxAge: 60 * 60 * 24 * 60,
    });

    setCookie("leadEmail", data.email.toString(), {
      maxAge: 60 * 60 * 24 * 60,
    });

    setCookie(
      "LeadhasTradeInVehicle",
      data.hasTradeInVehicle?.toString() ?? "false",
      {
        maxAge: 60 * 60 * 24 * 60,
      }
    );
  };

  const handleFormSubmit: SubmitHandler<LeadFormDataType> = (data) => {
    setLeadCookies(data);

    const defaultMessage = `Olá me chamo ${data.name}, estou interessado no veículo ${vehicle.make} ${vehicle.model} - ${vehicle.version}, do seu estoque no Click Autos.`;

    if (currentAction === ActionsTypeEnum.WHATSAPP && seller.whatsApp) {
      const encodedFullMessage = encodeURIComponent(
        data.message ? data.message : defaultMessage
      );

      window.open(
        `https://wa.me/55${removeNonNumericAndSpaces(
          String(seller.whatsApp)
        )}?text=${encodedFullMessage}`,
        "_blank"
      );

      sendPostHogEvent({
        eventName: PostHostEventsEnum.SEND_LEAD_WHATSAPP,
        email: data.email,
        eventProperties: {
          ...vehicle,
          ...data,
        },
      });

      toast({
        variant: "success",
        title: "Enviado.",
        description: "Tudo pronto para iniciar a conversa no whatsapp",
      });
    } else {
      sendPostHogEvent({
        eventName: PostHostEventsEnum.SEND_LEAD_MESSAGE,
        email: data.email,
        eventProperties: {
          ...vehicle,
          ...data,
        },
      });

      toast({
        variant: "success",
        title: "Mensagem enviada com sucesso!",
        description: "Um vendedor entrará em contato com você",
      });
    }

    const currentDate = new Date().toISOString();

    createLead({
      teamId: vehicle.teamId,
      vehicleId: vehicle.id,
      creationTime: currentDate,
      lastModificationTime: currentDate,
      leadOrigin: LeadOrigin.SEARCH_SIMULATION,
      status: LeadStatus.RECEIVED,
      hasTradeInVehicle: data.hasTradeInVehicle ?? false,
      message: data.message ?? defaultMessage,
      vehicle: {
        id: vehicle.id,
        price: vehicle.price as number,
        manufactureYear: vehicle.manufactureYear,
        modelYear: vehicle.modelYear,
        condition: vehicle.condition,
        kilometers: vehicle.kilometers,
        make: vehicle.make,
        model: vehicle.model,
        version: vehicle.version,
        plate: vehicle.plate,
      },
      contact: {
        name: data.name,
        phone: data.phone,
        email: data.email,
      },
    });

    setIsDialogOpen(false);
    reset();
  };

  const handleActionClick = (action: ActionsTypeEnum) => {
    setCurrentAction(action);
    setIsDialogOpen(true);
  };

  const handleClickCall = async () => {
    const email = (await getCookie("leadEmail")) ?? undefined;
    sendPostHogEvent({
      eventName: PostHostEventsEnum.SEND_CLICK_CALL,
      email: email,
      eventProperties: {
        ...vehicle,
      },
    });
  };

  const handleClickLocation = async ({ address }: { address: string }) => {
    const email = (await getCookie("leadEmail")) ?? undefined;
    sendPostHogEvent({
      eventName: PostHostEventsEnum.LOCATION_EVENT,
      email: email,
      eventProperties: {
        ...vehicle,
        address: address,
      },
    });
  };

  return (
    <div className="mt-12 pt-7 px-7 pb-6 border border-solid rounded-xl shadow max-h-[480px]">
      <div className="border border-solid w-20 h-20 leading-[75px] text-center rounded-full mb-3.5 relative overflow-hidden">
        <Image
          src={
            seller.logo
              ? `https://cdn.click.autos/${seller.logo}`
              : `https://api.dicebear.com/9.x/initials/png?seed=${seller.name}&fontFamily=Tahoma&backgroundColor=fbbf24`
          }
          alt={`Logo ${seller.name}`}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className="object-cover"
          priority
        />
      </div>
      <div>
        <h4 className="mb-1.5 font-medium text-xl relative">{seller.name}</h4>
        <div className="text-sm mb-3">{address}</div>

        <ul className=" mt-0 mb-7 list-inside flex items-center justify-center gap-2">
          <li className="mr-4">
            <Link
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline cursor-pointer transition duration-300 text-sm flex items-center"
              onClick={() => handleClickLocation({ address })}
            >
              <div className="w-10 h-10 leading-10 text-center rounded-full bg-primary-100 mr-2.5 flex items-center justify-center">
                <MapPin strokeWidth={1.5} color="#818cf8" />
              </div>
              Localização
            </Link>
          </li>
          <li>
            <Link
              href={`tel:+55${seller.whatsApp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline cursor-pointer transition duration-300 text-sm flex items-center"
              onClick={handleClickCall}
            >
              <div className="w-10 h-10 leading-10 text-center rounded-full bg-[rgb(233,_242,_255)] mr-2.5 flex items-center justify-center">
                <PhoneCall strokeWidth={1.5} color="#818cf8" />
              </div>
              Ligar Agora
            </Link>
          </li>
        </ul>
        <div>
          <button
            onClick={() => handleActionClick(ActionsTypeEnum.MESSAGE)}
            className="no-underline font-medium cursor-pointer transition duration-300 text-base h-14 leading-14 text-center sm:min-w-[350px] rounded-lg mb-5 bg-primary-600 flex items-center justify-center text-white hover:bg-primary-600/90 w-full"
          >
            Enviar mensagem
            <SendIcon />
          </button>

          <button
            onClick={() => handleActionClick(ActionsTypeEnum.WHATSAPP)}
            className="text-[rgb(96,_201,_97)] no-underline font-medium cursor-pointer transition duration-300 border border-solid border-[rgb(96,201,97)] text-base h-14 leading-14 text-center w-full rounded-lg flex items-center justify-center hover:bg-[rgb(96,201,97)] hover:text-white mb-5"
          >
            Chat Via Whatsapp
            <SendIcon />
          </button>

          <Link
            href={teamSlug ? `/loja/${teamSlug}` : `/lojas/${vehicle.teamId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline font-medium cursor-pointer transition duration-300 text-base w-full text-center flex items-center justify-center"
          >
            Ver todo o estoque do vendedor
            <SendIcon />
          </Link>
        </div>
      </div>
      <ContactDialog
        setValue={setValue}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          reset();
        }}
        onSubmit={handleSubmit(handleFormSubmit)}
        register={register}
        errors={errors}
        title={
          currentAction === ActionsTypeEnum.WHATSAPP
            ? "Chame no Whatsapp"
            : "Envie uma mensagem"
        }
        description="Envie uma mensagem ao vendedor"
        defaultMessage={`Olá, estou interessado ${vehicle.type === "Motorcycle" ? "na moto" : "no carro"} ${vehicle.make} ${vehicle.model} ${vehicle.version ? `- ${vehicle.version}` : ""}, do seu estoque no Click Autos.`}
      />
    </div>
  );
};
